export interface PlanetCivEntry {
    id: string;
    system: string;
    population: number;
    civLevel: number;
}

interface ProductionMeta {
    rawValue?: number;
    prodModifier?: number;
    timeFactor?: number | null;
}

export interface EstimateEntityTypeData {
    id: string;
    class?: string;
    length?: number;
    materials?: Record<string, number>;
    production?: ProductionMeta;
}

export interface EstimateCatalogs {
    ships: Record<string, EstimateEntityTypeData>;
    vehicles: Record<string, EstimateEntityTypeData>;
    items: Record<string, EstimateEntityTypeData>;
    droids: Record<string, EstimateEntityTypeData>;
}

export interface CivLookup {
    byPlanet: Record<string, number>;
    bySystem: Record<string, number>;
}

export interface QueueEstimate {
    status: 'ok' | 'missing_data';
    reason?: string;
    daysMin?: number;
    daysMax?: number;
    secondsMin?: number;
    secondsMax?: number;
    startMinAt?: number;
    startMaxAt?: number;
    endMinAt?: number;
    endMaxAt?: number;
}

export interface QueueEstimateResult {
    estimate: QueueEstimate;
    nextStartMinAt: number;
    nextStartMaxAt: number;
}

export interface QueueLikeItem {
    quantity: number;
    entityId: string;
    entityHref?: string;
}

export interface LocationLike {
    systemType?: string;
    systemId?: string;
    planetId?: string;
}


const MANAGEMENT_MIN = 0;
const MANAGEMENT_MAX = 5;
const NPC_MOD = 1.0;

function indexById(rows: EstimateEntityTypeData[]): Record<string, EstimateEntityTypeData> {
    return rows.reduce((acc, row) => {
        if (row?.id) {
            acc[row.id] = row;
        }
        return acc;
    }, {} as Record<string, EstimateEntityTypeData>);
}

export function buildEstimateCatalogs(raw: {
    ships: EstimateEntityTypeData[];
    vehicles: EstimateEntityTypeData[];
    items: EstimateEntityTypeData[];
    droids: EstimateEntityTypeData[];
}): EstimateCatalogs {
    return {
        ships: indexById(raw.ships),
        vehicles: indexById(raw.vehicles),
        items: indexById(raw.items),
        droids: indexById(raw.droids),
    };
}

export function buildCivLookup(planets: PlanetCivEntry[]): CivLookup {
    const byPlanet: Record<string, number> = {};
    const systemTotals: Record<string, {sum: number; count: number}> = {};

    for (const planet of planets) {
        byPlanet[planet.id] = planet.civLevel ?? 0;
        if (!planet.system || (planet.population ?? 0) <= 0) {
            continue;
        }
        if (!systemTotals[planet.system]) {
            systemTotals[planet.system] = {sum: 0, count: 0};
        }
        systemTotals[planet.system].sum += planet.civLevel ?? 0;
        systemTotals[planet.system].count += 1;
    }

    const bySystem: Record<string, number> = {};
    Object.entries(systemTotals).forEach(([systemId, value]) => {
        bySystem[systemId] = value.count > 0 ? value.sum / value.count : 0;
    });

    return {byPlanet, bySystem};
}

function parseTypeFromHref(href?: string): 'ships' | 'vehicles' | 'items' | 'droids' | 'weapons' | null {
    if (!href) {
        return null;
    }
    const match = /\/types\/([^/]+)\//.exec(href);
    if (!match) {
        return null;
    }
    const type = match[1]?.toLowerCase();
    if (type === 'ships' || type === 'vehicles' || type === 'items' || type === 'droids' || type === 'weapons') {
        return type;
    }
    return null;
}

function parseTypeFromEntityId(entityId: string): 'ships' | 'vehicles' | 'items' | 'droids' | null {
    const prefix = entityId.split(':')[0];
    if (prefix === '2') return 'ships';
    if (prefix === '3') return 'vehicles';
    if (prefix === '12') return 'items';
    if (prefix === '13') return 'droids';
    return null;
}

function resolveTargetType(item: QueueLikeItem): 'ships' | 'vehicles' | 'items' | 'droids' | 'weapons' | null {
    return parseTypeFromHref(item.entityHref) ?? parseTypeFromEntityId(item.entityId);
}

function getEntityData(type: 'ships' | 'vehicles' | 'items' | 'droids', entityId: string, catalogs: EstimateCatalogs): EstimateEntityTypeData | null {
    return catalogs[type][entityId] ?? null;
}

function totalMaterials(type: 'ships' | 'vehicles' | 'items' | 'droids', entityData: EstimateEntityTypeData): number | null {
    if (type === 'items') {
        const rmp = entityData.production?.rawValue ?? 0;
        if (rmp <= 0) {
            return null;
        }
        return 2.5 * Math.pow(rmp, 0.4);
    }

    const materials = entityData.materials ?? {};
    const values = Object.values(materials);
    if (values.length === 0) {
        return null;
    }
    const total = values.reduce((sum, value) => sum + Number(value || 0), 0);
    return total > 0 ? total : null;
}

function shipCategoryModifier(shipData: EstimateEntityTypeData): number {
    const className = (shipData.class ?? '').toLowerCase();
    const length = Number(shipData.length ?? 0);

    if (className.includes('capital')) {
        if (length >= 3000) return 1.5;
        if (length >= 200) return 2.0;
        return 3.5;
    }
    if (className.includes('freighter')) return 2.0;
    if (className.includes('fighter') || className.includes('satellite')) return 2.0;
    return 1.0;
}

function categoryModifier(type: 'ships' | 'vehicles' | 'items' | 'droids', entityData: EstimateEntityTypeData): number {
    const timeFactor = Number(entityData.production?.timeFactor ?? 0);
    if (timeFactor > 0) return timeFactor;
    // Fallback to the rules values for types the SWC API doesn't provide a timeFactor for
    if (type === 'ships') return shipCategoryModifier(entityData);
    return 2.0;
}

export function resolveCivLevel(location: LocationLike, civLookup: CivLookup): number {
    const systemType = (location.systemType ?? '').toLowerCase();
    if (systemType.includes('deep space')) {
        return 0;
    }

    const planetId = location.planetId ?? '';
    if (planetId && typeof civLookup.byPlanet[planetId] === 'number') {
        return civLookup.byPlanet[planetId];
    }

    const systemId = location.systemId ?? '';
    if (systemId && typeof civLookup.bySystem[systemId] === 'number') {
        return civLookup.bySystem[systemId];
    }
    return 0;
}

export function estimateQueuedItem(
    item: QueueLikeItem,
    holderEntityType: string,
    location: LocationLike,
    catalogs: EstimateCatalogs,
    civLookup: CivLookup,
    startMinAt: number,
    startMaxAt: number,
    managementSkill?: number | null
): QueueEstimateResult {
    const quantity = Math.max(1, Number(item.quantity || 1));
    const targetType = resolveTargetType(item);
    if (!targetType || targetType === 'weapons') {
        return {
            estimate: {status: 'missing_data', reason: 'No supported produced type for estimate'},
            nextStartMinAt: startMinAt,
            nextStartMaxAt: startMaxAt,
        };
    }

    const entityData = getEntityData(targetType, item.entityId, catalogs);
    if (!entityData) {
        return {
            estimate: {status: 'missing_data', reason: 'Produced entity type data not found'},
            nextStartMinAt: startMinAt,
            nextStartMaxAt: startMaxAt,
        };
    }

    const productionMod = Number(entityData.production?.prodModifier ?? 0);
    if (productionMod <= 0) {
        return {
            estimate: {status: 'missing_data', reason: 'Missing production modifier'},
            nextStartMinAt: startMinAt,
            nextStartMaxAt: startMaxAt,
        };
    }

    const mats = totalMaterials(targetType, entityData);
    if (!mats || mats <= 0) {
        console.error('Missing material/RMP data for estimate');
        return {
            estimate: {status: 'missing_data', reason: 'Missing material/RMP data for estimate'},
            nextStartMinAt: startMinAt,
            nextStartMaxAt: startMaxAt,
        };
    }

    const civ = resolveCivLevel(location, civLookup)/100;
    const civFactor = Math.max(0, 1 - civ / 8);
    const locationMod = holderEntityType === 'facility' ? 1 : 0.9;
    const extraMod = categoryModifier(targetType, entityData);
    const mpf = Math.pow(quantity, -0.04);

    const samples: number[] = [];
    const hasExactManagement = typeof managementSkill === 'number' && Number.isFinite(managementSkill);
    const clampedManagement = hasExactManagement
        ? Math.max(MANAGEMENT_MIN, Math.min(MANAGEMENT_MAX, Number(managementSkill)))
        : null;
    const managementValues = hasExactManagement && clampedManagement !== null
        ? [clampedManagement]
        : [MANAGEMENT_MIN, MANAGEMENT_MAX];

    for (const management of managementValues) {
        const days = ((((mats / productionMod) * (1.2 - management / 25)) / NPC_MOD) * civFactor)
            * locationMod
            * quantity
            * mpf
            * extraMod;
        if (days > 0) {
            samples.push(days);
        }
    }

    if (samples.length === 0) {
        return {
            estimate: {status: 'missing_data', reason: 'Estimate formula resulted in non-positive duration'},
            nextStartMinAt: startMinAt,
            nextStartMaxAt: startMaxAt,
        };
    }

    const daysMin = Math.min(...samples);
    const daysMax = Math.max(...samples);
    const secondsMin = Math.max(1, Math.round(daysMin * 86400));
    const secondsMax = Math.max(secondsMin, Math.round(daysMax * 86400));
    const endMinAt = startMinAt + secondsMin;
    const endMaxAt = startMaxAt + secondsMax;

    return {
        estimate: {
            status: 'ok',
            daysMin,
            daysMax,
            secondsMin,
            secondsMax,
            startMinAt,
            startMaxAt,
            endMinAt,
            endMaxAt,
        },
        nextStartMinAt: endMinAt,
        nextStartMaxAt: endMaxAt,
    };
}
